import { useEffect, useRef } from 'react';
import { identifyUser } from '../components/Analytics';
import { debugLog } from '../utils/debugUtils';

/**
 * Global User Identification Hook
 *
 * Automatically identifies users across all analytics platforms when:
 * - App loads with stored email data
 * - Route changes and email data is available
 * - Email data becomes available during session
 *
 * This ensures consistent user identification without requiring explicit
 * identifyUser calls throughout the codebase.
 */
export const useGlobalUserIdentification = () => {
  const lastIdentifiedEmailRef = useRef(null);
  const identificationTimeoutRef = useRef(null);

  useEffect(() => {
    const checkAndIdentifyUser = async () => {
      if (typeof window === 'undefined') return;

      try {
        // Get current user data
        const { getUserData } = await import('../utils/userDataPersistence');
        const userData = getUserData();
        const currentEmail = userData?.email;

        // Only identify if we have an email and it's different from last identified
        if (currentEmail && currentEmail !== lastIdentifiedEmailRef.current) {
          debugLog('🎯 Global user identification triggered:', {
            email: `${currentEmail.substring(0, 3)}***`,
            previousEmail: lastIdentifiedEmailRef.current
              ? `${lastIdentifiedEmailRef.current.substring(0, 3)}***`
              : 'none',
            trigger: 'automatic_identification',
          });

          // Identify user across all platforms
          identifyUser(currentEmail);

          // Track that we've identified this email
          lastIdentifiedEmailRef.current = currentEmail;
        }
      } catch (error) {
        debugLog('Failed to check user data for identification:', error);
      }
    };

    // Check immediately on mount
    checkAndIdentifyUser();

    // Set up periodic check for email data changes (every 30 seconds)
    // This catches cases where email is stored after initial load
    const intervalId = setInterval(checkAndIdentifyUser, 30000);

    // Also check when visibility changes (user returns to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Clear any existing timeout and check after a brief delay
        if (identificationTimeoutRef.current) {
          clearTimeout(identificationTimeoutRef.current);
        }
        identificationTimeoutRef.current = setTimeout(
          checkAndIdentifyUser,
          1000
        );
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (identificationTimeoutRef.current) {
        clearTimeout(identificationTimeoutRef.current);
      }
    };
  }, []);

  // Return method for manual identification (useful for explicit calls)
  const identifyIfNeeded = async email => {
    if (email && email !== lastIdentifiedEmailRef.current) {
      debugLog('🎯 Manual user identification triggered:', {
        email: `${email.substring(0, 3)}***`,
        trigger: 'manual_call',
      });
      identifyUser(email);
      lastIdentifiedEmailRef.current = email;
    }
  };

  return { identifyIfNeeded };
};
